import { useEffect, useMemo, useState, MouseEvent } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Popover,
  Tooltip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  searchAll,
  searchAdvanced,
  createSavedSearch,
  listSavedSearches,
  AdvancedSearchFilters,
  SavedSearchFilters,
  SavedSearchDisplayFilters,
} from "../data/request";
import { SearchResultList } from "./Components/SearchResultList";
import { SearchResult } from "../types/SearchResult";
import { useAuth } from "../auth/AuthContext";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY, BORDER_GREY } from "./colors";
import { EnterprisePill } from "./Components/EnterprisePill";
import { ProPill } from "./Components/ProPill";
import { SIC } from "../data/utilities";

type AdvancedResult = {
  company_number: string;
  company_name: string;
  company_status: string;
  incorporation_date: string;
  sic_code: string;
  sic_description: string;
  post_town: string;
  county: string;
  postcode: string;
};

// One clearly-worded, normal-case option per distinct status value Companies
// House's live advanced-search endpoint actually supports (confirmed against
// lambda_search_advanced.py's STATUS_MAP: active / liquidation /
// administration / voluntary-arrangement / receivership). Several labels
// this list used to carry (e.g. "RECEIVERSHIP", "Live but Receiver Manager on
// at least one charge", "Administrative Receiver") collapsed onto the exact
// same underlying CH value as one of the five kept here, so they were
// removed rather than kept as confusing duplicates - a dropdown still beats
// free text since there's no way to guess CH's exact backend spelling
// otherwise, but every remaining entry here now maps to a genuinely distinct
// result set.
const STATUS_OPTIONS = ["Active", "Liquidation", "In Administration", "Voluntary Arrangement", "Receivership"];

const isoDaysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const EXAMPLES: { label: string; filters: AdvancedSearchFilters }[] = [
  { label: "Incorporated in the last 7 days", filters: { incorporatedAfter: isoDaysAgo(7) } },
  { label: "Incorporated in the last 30 days", filters: { incorporatedAfter: isoDaysAgo(30) } },
  { label: "Incorporated in the last year", filters: { incorporatedAfter: isoDaysAgo(365) } },
  { label: "Distressed (In Administration)", filters: { status: "In Administration" } },
  { label: "In Liquidation", filters: { status: "Liquidation" } },
  { label: "In receivership", filters: { status: "Receivership" } },
  { label: "IT consultancy companies", filters: { sicCode: "62020" } },
  { label: "Holding companies", filters: { sicCode: "64209" } },
  { label: "Companies in London", filters: { location: "London" } },
];

type SicOption = { code: string; label: string };
const SIC_OPTIONS: SicOption[] = Object.entries(SIC as Record<string, string>).map(([code, description]) => ({
  code,
  label: `${code} - ${description}`,
}));

// Any advanced filter beyond a plain name means the search needs our own
// Athena-backed index (Companies House's search API has no way to filter by
// SIC/status/location/date) - a name-only search stays on the fast, existing
// path instead of paying Athena's few-seconds-per-query cost for no reason.
const hasAdvancedFilter = (filters: AdvancedSearchFilters) =>
  Boolean(filters.sicCode || filters.status || filters.location || filters.incorporatedAfter || filters.incorporatedBefore);

// Dynamic Portfolios only ever store sic_code/status/location/name - a search
// that's *only* a date range (see toSavedSearchFilters below) has nothing left
// to save once the date is correctly stripped. This is used to disable the
// Save button with an explanation instead of letting the user hit a bare
// "No filters" result after saving.
const hasSavableFilter = (filters: AdvancedSearchFilters) =>
  Boolean(filters.sicCode || filters.status || filters.location || filters.name);

// A saved search stores the backend's CH-facing snake_case filter keys (same
// shape searchAdvanced sends over the wire), not this page's internal
// camelCase AdvancedSearchFilters state - and never the date range, which the
// backend recomputes fresh against "now" on every run rather than storing.
const toSavedSearchFilters = (filters: AdvancedSearchFilters): SavedSearchFilters => {
  const out: SavedSearchFilters = {};
  if (filters.name) out.name = filters.name;
  if (filters.sicCode) out.sic_code = filters.sicCode;
  if (filters.status) out.status = filters.status;
  if (filters.location) out.location = filters.location;
  return out;
};

const defaultSavedSearchName = (filters: AdvancedSearchFilters) => {
  const parts: string[] = [];
  if (filters.sicCode) parts.push(`SIC ${filters.sicCode}`);
  if (filters.status) parts.push(filters.status);
  if (filters.location) parts.push(filters.location);
  if (filters.name) parts.push(filters.name);
  return parts.length > 0 ? parts.join(", ") : "Dynamic Portfolio";
};

type SavedSearch = { search_id: string; name: string; filters: SavedSearchFilters };

// Field-by-field comparison, not a JSON.stringify shortcut - key order isn't
// guaranteed to match between what was just built here and what came back
// from the backend, and an absent field should compare equal to an empty
// string either way.
const savedSearchFiltersEqual = (a: SavedSearchFilters, b: SavedSearchFilters) =>
  (a.name ?? "") === (b.name ?? "") &&
  (a.sic_code ?? "") === (b.sic_code ?? "") &&
  (a.status ?? "") === (b.status ?? "") &&
  (a.location ?? "") === (b.location ?? "");

export const SearchResultPage = () => {
  const auth = useAuth();
  const { searchText } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [filters, setFilters] = useState<AdvancedSearchFilters>({});
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  const [simpleResults, setSimpleResults] = useState<SearchResult[]>([]);
  const [advancedResults, setAdvancedResults] = useState<AdvancedResult[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [saveAnchorEl, setSaveAnchorEl] = useState<HTMLElement | null>(null);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState<string>();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>();
  // Collapsed by default, same Accordion/AccordionSummary/AccordionDetails
  // pattern as Home.tsx's pricingExpanded - Advanced Filters is a Pro feature
  // most visits don't need open, so it starts out of the way rather than
  // taking up the top of the page on every search.
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(false);

  const runSearch = async (toSearch: AdvancedSearchFilters) => {
    setError(undefined);
    setLoading(true);
    try {
      if (hasAdvancedFilter(toSearch)) {
        setMode("advanced");
        const doc = await searchAdvanced(toSearch);
        if (doc?.error) {
          setError(doc.error);
          setAdvancedResults([]);
        } else {
          setAdvancedResults(doc?.items ?? []);
        }
      } else {
        setMode("simple");
        const doc = await searchAll(toSearch.name ?? "");
        setSimpleResults(doc?.items ?? []);
      }
    } catch {
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // "Run this search again" deep link from DynamicPortfoliosPanel - filters
    // travel via router state, not the URL, so this takes priority over the
    // URL-derived filters below whenever present (the :searchText param in
    // that case is just a readable slug, not the real filter source).
    const stateFilters = (location.state as { filters?: AdvancedSearchFilters } | null)?.filters;
    if (stateFilters) {
      setFilters(stateFilters);
      runSearch(stateFilters);
      return;
    }

    const sicFromUrl = searchParams.get("sic_code");
    const initial: AdvancedSearchFilters = { name: searchText };
    if (sicFromUrl && auth.isPro) {
      initial.sicCode = sicFromUrl;
    }
    setFilters(initial);
    if (initial.name || initial.sicCode) {
      runSearch(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, searchParams, location.state]);

  // Enterprise-only "already saved" indicator - fetched once (not re-fetched
  // per filter change) and compared client-side against whatever the current
  // filters are, since the set of saved searches only changes when the user
  // saves a new one (handled by the optimistic append in handleSaveSearch).
  useEffect(() => {
    if (auth.isEnterprise) {
      listSavedSearches().then((doc) => setSavedSearches(doc?.saved_searches ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isEnterprise]);

  const selectedSicOption = useMemo(
    () => SIC_OPTIONS.find((o) => o.code === filters.sicCode) ?? null,
    [filters.sicCode]
  );

  // Deliberately kept off this page as a small note, not a full saved-searches
  // list - see the source plan's design discussion. Only ever set once
  // savedSearches has loaded, so it stays undefined (no indicator) until then.
  const matchingSavedSearch = useMemo(() => {
    if (!savedSearches) return undefined;
    const current = toSavedSearchFilters(filters);
    return savedSearches.find((s) => savedSearchFiltersEqual(s.filters ?? {}, current));
  }, [savedSearches, filters]);

  const handleExample = (exampleFilters: AdvancedSearchFilters) => {
    const merged = { name: filters.name, ...exampleFilters };
    setFilters(merged);
    runSearch(merged);
  };

  const handleOpenSaveSearch = (e: MouseEvent<HTMLElement>) => {
    setSaveConfirmation(undefined);
    setSaveName(defaultSavedSearchName(filters));
    setSaveAnchorEl(e.currentTarget);
  };

  const handleSaveSearch = async () => {
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const savedFilters = toSavedSearchFilters(filters);
      // Whatever date filter happens to be active in the UI right now, at
      // the moment "Save as Dynamic Portfolio" is clicked - captured purely
      // for display on the saved search's card (see
      // DynamicPortfoliosPanel.tsx's summariseFilters). Never part of
      // savedFilters/toSavedSearchFilters above, which is what the backend
      // actually matches against going forward - Dynamic Portfolios always
      // check "what's new since last time", never a fixed date window.
      const displayFilters: SavedSearchDisplayFilters = {};
      if (filters.incorporatedAfter) displayFilters.incorporated_after = filters.incorporatedAfter;
      if (filters.incorporatedBefore) displayFilters.incorporated_before = filters.incorporatedBefore;
      const doc = await createSavedSearch(name, savedFilters, displayFilters);
      setSaveAnchorEl(null);
      if (doc?.error) {
        setError(doc.error);
      } else {
        setError(undefined);
        setSaveConfirmation(`Saved as "${name}"`);
        setTimeout(() => setSaveConfirmation(undefined), 3000);
        // Optimistic append so the "already saved" indicator appears
        // immediately, without waiting on a re-fetch of listSavedSearches().
        if (doc?.search_id) {
          setSavedSearches([
            ...(savedSearches ?? []),
            { search_id: doc.search_id, name: doc.name ?? name, filters: doc.filters ?? savedFilters },
          ]);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box style={{ padding: 20 }}>
        <Box style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <TextField
            label="Company Name"
            size="small"
            value={filters.name ?? ""}
            onChange={(e) => setFilters({ ...filters, name: e.target.value || undefined })}
            onKeyDown={(e) => e.key === "Enter" && runSearch(filters)}
            style={{ flex: 1 }}
          />
          <Button variant="contained" onClick={() => runSearch(filters)} disabled={loading} style={{ backgroundColor: DELTA_ACCENT }}>
            Search
          </Button>
        </Box>

        <Paper
          variant="outlined"
          style={{
            borderColor: BORDER_GREY,
            marginBottom: 16,
            opacity: auth.isPro ? 1 : 0.6,
          }}
        >
          <Accordion
            expanded={advancedFiltersExpanded}
            onChange={(_, expanded) => setAdvancedFiltersExpanded(expanded)}
            elevation={0}
            style={{ background: "transparent" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography variant="subtitle2" style={{ color: DELTA_PRIMARY, fontWeight: 700 }}>
                  Advanced Filters
                </Typography>
                {!auth.isPro && <ProPill />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
          <Box style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Autocomplete
              options={SIC_OPTIONS}
              value={selectedSicOption}
              onChange={(_, option) => setFilters({ ...filters, sicCode: option?.code })}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              size="small"
              disabled={!auth.isPro}
              style={{ width: 320 }}
              renderInput={(params) => <TextField {...params} label="SIC Code (type to search)" />}
            />

            <TextField
              select
              label="Status"
              size="small"
              disabled={!auth.isPro}
              value={filters.status ?? ""}
              onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
              style={{ width: 220 }}
            >
              <MenuItem value="">Any</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Location (postcode, town, or county)"
              size="small"
              disabled={!auth.isPro}
              value={filters.location ?? ""}
              onChange={(e) => setFilters({ ...filters, location: e.target.value || undefined })}
              placeholder="e.g. SW1, Poole, Dorset"
              style={{ width: 220 }}
            />

            <TextField
              label="Incorporated After"
              type="date"
              size="small"
              disabled={!auth.isPro}
              value={filters.incorporatedAfter ?? ""}
              onChange={(e) => setFilters({ ...filters, incorporatedAfter: e.target.value || undefined })}
              InputLabelProps={{ shrink: true }}
              style={{ width: 180 }}
            />
            <TextField
              label="Incorporated Before"
              type="date"
              size="small"
              disabled={!auth.isPro}
              value={filters.incorporatedBefore ?? ""}
              onChange={(e) => setFilters({ ...filters, incorporatedBefore: e.target.value || undefined })}
              InputLabelProps={{ shrink: true }}
              style={{ width: 180 }}
            />
          </Box>

          <Box style={{ marginTop: 16 }}>
            <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY, marginBottom: 8 }}>
              Examples
            </Typography>
            <Box style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {EXAMPLES.map((ex) => (
                <Chip
                  key={ex.label}
                  label={ex.label}
                  size="small"
                  variant="outlined"
                  disabled={!auth.isPro}
                  onClick={() => handleExample(ex.filters)}
                  style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
                />
              ))}
            </Box>
          </Box>

          {auth.isPro && hasAdvancedFilter(filters) && (
            <Box style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
              {auth.isEnterprise && matchingSavedSearch ? (
                <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                  Already saved as a Dynamic Portfolio ("{matchingSavedSearch.name}") —{" "}
                  <RouterLink
                    to="/portfolio"
                    state={{ tab: "dynamic", searchId: matchingSavedSearch.search_id }}
                    style={{ color: DELTA_ACCENT }}
                  >
                    view it
                  </RouterLink>
                </Typography>
              ) : (
                <>
                  <Tooltip
                    title={
                      !auth.isEnterprise
                        ? "Upgrade to Enterprise to save this search as a Dynamic Portfolio and get emailed when a new company matches"
                        : !hasSavableFilter(filters)
                        ? "Add a SIC code, status, or location filter to save this - date ranges aren't stored, since Dynamic Portfolios always check for what's new since your last check, not a fixed window."
                        : ""
                    }
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!auth.isEnterprise || !hasSavableFilter(filters)}
                        onClick={handleOpenSaveSearch}
                        style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
                      >
                        Save as Dynamic Portfolio
                      </Button>
                      {!auth.isEnterprise && <EnterprisePill />}
                    </span>
                  </Tooltip>
                  <Typography variant="caption" style={{ color: TEXT_SECONDARY }}>
                    Get emailed when a new company matches this search.
                  </Typography>
                </>
              )}
              {saveConfirmation && (
                <Typography variant="body2" style={{ color: "#2E7D32" }}>
                  {saveConfirmation}
                </Typography>
              )}
            </Box>
          )}
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Box>

      {loading && (
        <Box style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Typography variant="body2" style={{ color: "#C62828", padding: "0 20px 20px" }}>
          {error}
        </Typography>
      )}

      {!loading && mode === "simple" && <SearchResultList data={simpleResults} />}

      {!loading &&
        mode === "advanced" &&
        advancedResults &&
        (advancedResults.length === 0 ? (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, padding: "0 20px" }}>
            No companies match those filters.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Incorporated</TableCell>
                <TableCell>SIC Code</TableCell>
                <TableCell>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {advancedResults.map((r) => (
                <TableRow key={r.company_number}>
                  <TableCell>
                    <RouterLink to={`/company/${r.company_number}`} style={{ color: DELTA_ACCENT, textDecoration: "none", fontWeight: 600 }}>
                      {r.company_name}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{r.company_status}</TableCell>
                  <TableCell>{r.incorporation_date}</TableCell>
                  <TableCell>{r.sic_description ? `${r.sic_code} - ${r.sic_description}` : r.sic_code}</TableCell>
                  <TableCell>{[r.post_town, r.county, r.postcode].filter(Boolean).join(", ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}

      <Popover
        open={Boolean(saveAnchorEl)}
        anchorEl={saveAnchorEl}
        onClose={() => setSaveAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box style={{ padding: 16, width: 280 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Save as Dynamic Portfolio
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Dynamic Portfolio Name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSaveSearch()}
            style={{ marginBottom: 12 }}
          />
          <Box style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button size="small" onClick={() => setSaveAnchorEl(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={saving || !saveName.trim()}
              onClick={handleSaveSearch}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default SearchResultPage;
